import { ReactElement, useEffect, useState } from "react";
import { GetRevenueSummaryUnitTypeQuery, OriginalRevenueModel } from "../../../../__generated__/generated_types";
import { Property } from "../../../../contexts/properties/PropertiesContext";
import { Body, Cell, Head, HeaderRow, Row, Table } from "@zendeskgarden/react-tables";
import { buildCells, buildCellsCsv, buildHeaderCells, buildHeaderCellsCsv, buildNumbers, buildPropertyNumbersSum, buildPropertyNumbersWeightedAverage, buildRatio, buildTradeOutPercent, buildTradeOutPercentCellsCsv } from "./content_builders";
import * as css from "./revenueSummary.module.scss";
import { Dropdown, Field, Item, Menu, Select } from "@zendeskgarden/react-dropdowns";
import { Button } from "@zendeskgarden/react-buttons";
import FileSaver from "file-saver";
import { formatterIntNoGrouping, formatterPercentNoGrouping, formatterDecimalNoGrouping, formatterInt, formatterPercent, formatterDollarUSNoDecimal, formatExportTimestampSlug } from "../../../../utils/formatters";
import tableCSS from "../../../property-executive-summary/styles/table.module.scss";
import cn from "classnames";
import {propertyDisplayBudgetYear} from "../../../../contexts/properties/logic";
import {getMonths} from "../../../../utils/months-helper";

export type RevenueSummaryUnitType = { unitTypeId: string, unitTypeName: string };
export type IRevenueSummaryTableProps = {
    unitTypeData: GetRevenueSummaryUnitTypeQuery["originalRevenueModel"],
    property: Property,
    unitTypes: RevenueSummaryUnitType[] | undefined,
    showLabel?: boolean,
    hideReforecast?: boolean
}

export type UnitTypeFieldKey = keyof Pick<OriginalRevenueModel,
    "unitTypeAvgMarketRent" |
    "unitTypeExpirationCount" |
    "unitTypeInPlaceRent" |
    "unitTypeMoveInCount" |
    "unitTypeMoveOutCount" |
    "unitTypeNewLeaseTradeOut" |
    "unitTypeOccupiedCount" |
    "unitTypeRenewalCount" |
    "unitTypeRenewalTradeOut" |
    "unitTypeUnitCountMonthly" |
    "unitTypeVacancyCount"
>;


export function RevenueSummaryTable(props: IRevenueSummaryTableProps): ReactElement {

    const hideReforecast = props.hideReforecast ?? false;

    const [expanded, setExpanded] = useState<Record<UnitTypeFieldKey, boolean>>({
        unitTypeAvgMarketRent: false,
        unitTypeExpirationCount: false,
        unitTypeInPlaceRent: false,
        unitTypeMoveInCount: false,
        unitTypeMoveOutCount: false,
        unitTypeNewLeaseTradeOut: false,
        unitTypeOccupiedCount: false,
        unitTypeRenewalCount: false,
        unitTypeRenewalTradeOut: false,
        unitTypeUnitCountMonthly: false,
        unitTypeVacancyCount: false,
    });
    const [selectedUnitType, setSelectedUnitType] = useState<string>("all");

    useEffect(() => {
        if (!props.property) {
            return;
        }

        setSelectedUnitType("all");
    }, [props.property]);

    function handleExpand(key: UnitTypeFieldKey) {
        setExpanded(prev => {
            const prevVal = prev[key];
            return {
                ...prev,
                [key]: !prevVal
            };
        });
    }

    function buildPropertyTradeOutPercent(
        revenueData: NonNullable<IRevenueSummaryTableProps["unitTypeData"]>,
        propertyNumbers: number[]
    ): (number | null)[] {
        return buildTradeOutPercent(
            propertyNumbers,
            buildPropertyNumbersWeightedAverage(
                props.property.reforecastStartMonthIndex,
                revenueData.unitTypeInPlaceRent,
                revenueData.unitTypeUnitCountMonthly,
                selectedUnitType)
        );
    }

    function TableBlockRowExpandable({
            label,
            unitTypeField,
            formatter,
            propertyAggregate = "sum",
            unitTypeFieldWeights,
            hideReforecast,
            showTradeOutPercent = false
        }:
        {
            label: string,
            unitTypeField: UnitTypeFieldKey,
            formatter: Intl.NumberFormat,
            propertyAggregate?: "sum" | "wavg" | "ratio",
            unitTypeFieldWeights?: UnitTypeFieldKey,
            hideReforecast: boolean,
            showTradeOutPercent?: boolean
        }
    ): ReactElement {
        const revenueData = props.unitTypeData;
        if (!revenueData) {
            return <></>;
        }

        let propertyNumbers: number[] = [];
        if (propertyAggregate == "sum") {
            propertyNumbers = buildPropertyNumbersSum(
                    props.property.reforecastStartMonthIndex,
                    revenueData[unitTypeField],
                    selectedUnitType);
        }
        else if (propertyAggregate == "wavg" && unitTypeFieldWeights) {
            propertyNumbers = buildPropertyNumbersWeightedAverage(
                    props.property.reforecastStartMonthIndex,
                    revenueData[unitTypeField],
                    revenueData[unitTypeFieldWeights],
                    selectedUnitType);
        }
        else if (propertyAggregate == "ratio" && unitTypeFieldWeights) {
            const numeratorPropertyNumbers = buildPropertyNumbersSum(
                    props.property.reforecastStartMonthIndex,
                    revenueData[unitTypeField],
                    selectedUnitType);
            const denominatorPropertyNumbers = buildPropertyNumbersSum(
                    props.property.reforecastStartMonthIndex,
                    revenueData[unitTypeFieldWeights],
                    selectedUnitType);
            propertyNumbers = buildRatio(numeratorPropertyNumbers, denominatorPropertyNumbers);
        }
        else {
            return <></>;
        }

        const inPlaceRentByUnitType = revenueData.unitTypeInPlaceRent.toIdMap("unitTypeId");

        return (
            <>
                <Row>
                    <Cell onClick={() => handleExpand(unitTypeField)} className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>
                        {expanded[unitTypeField] ? "-" : "+"} {label}
                        {showTradeOutPercent && <div className={css.tradeOutPercent}>{label} %</div>}
                    </Cell>
                    {buildCells(
                        props.property.reforecastStartMonthIndex,
                        propertyNumbers,
                        formatter,
                        hideReforecast,
                        props.property.isAcquisitionProperty,
                        showTradeOutPercent ? buildPropertyTradeOutPercent(revenueData, propertyNumbers) : undefined
                    )}
                </Row>
                {
                    expanded[unitTypeField] &&
                    revenueData[unitTypeField]
                        .filter(utData => selectedUnitType == "all" || utData.unitTypeId == selectedUnitType)
                        .sortBy("unitTypeName").map((utData, i) => {
                            const unitTypeNumbers = buildNumbers(props.property.reforecastStartMonthIndex, utData);
                            const unitTypeInPlaceRent = inPlaceRentByUnitType[utData.unitTypeId];
                            return (
                                <Row key={i}>
                                    <Cell className={cn(css.unitTypeName, tableCSS.fixedCol, tableCSS.firstCol)}> {utData.unitTypeName}</Cell>
                                    {buildCells(
                                        props.property.reforecastStartMonthIndex,
                                        unitTypeNumbers,
                                        formatter,
                                        hideReforecast,
                                        props.property.isAcquisitionProperty,
                                        showTradeOutPercent && unitTypeInPlaceRent
                                            ? buildTradeOutPercent(unitTypeNumbers, buildNumbers(props.property.reforecastStartMonthIndex, unitTypeInPlaceRent))
                                            : undefined
                                    )}
                                </Row>
                            );
                        })
                }
            </>
        );
    }


    function tableBlockRowRatioCsv({
            label,
            unitTypeNumeratorField,
            formatter,
            unitTypeDenominatorField
        }: {
            label: string,
            unitTypeNumeratorField: UnitTypeFieldKey,
            formatter: Intl.NumberFormat,
            unitTypeDenominatorField: UnitTypeFieldKey
    }): string[][] {
        const revenueData = props.unitTypeData;
        if (!props.property || !revenueData) {
            return [];
        }

        let propertyNumbers: number[] = [];
        const numeratorPropertyNumbers = buildPropertyNumbersSum(
                props.property.reforecastStartMonthIndex,
                revenueData[unitTypeNumeratorField],
                selectedUnitType);
        const denominatorPropertyNumbers = buildPropertyNumbersSum(
                props.property.reforecastStartMonthIndex,
                revenueData[unitTypeDenominatorField],
                selectedUnitType);
        propertyNumbers = buildRatio(numeratorPropertyNumbers, denominatorPropertyNumbers);
        return (
                [
                    [label,
                        ...buildCellsCsv(
                            props.property.reforecastStartMonthIndex,
                            propertyNumbers,
                            formatter
                        )
                    ]
                ]
        );
    }


    function TableBlockRowRatio({
            label,
            unitTypeNumeratorField,
            formatter,
            unitTypeDenominatorField,
            hideReforecast
    }:
    {
        label: string,
        unitTypeNumeratorField: UnitTypeFieldKey,
        formatter: Intl.NumberFormat,
        unitTypeDenominatorField: UnitTypeFieldKey,
        hideReforecast: boolean
    }
    ): ReactElement {
        const revenueData = props.unitTypeData;
        if (!props.property || !revenueData) {
            return <></>;
        }

        let propertyNumbers: number[] = [];
        const numeratorPropertyNumbers = buildPropertyNumbersSum(
                props.property.reforecastStartMonthIndex,
                revenueData[unitTypeNumeratorField],
                selectedUnitType);
        const denominatorPropertyNumbers = buildPropertyNumbersSum(
                props.property.reforecastStartMonthIndex,
                revenueData[unitTypeDenominatorField],
                selectedUnitType);
        propertyNumbers = buildRatio(numeratorPropertyNumbers, denominatorPropertyNumbers);
        return (
                <Row>
                    <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>{label}</Cell>
                    {buildCells(
                        props.property.reforecastStartMonthIndex,
                        propertyNumbers,
                        formatter,
                        hideReforecast,
                        props.property.isAcquisitionProperty,
                    )}
                </Row>
        );
    }

    function tableBlockRowExpandableCsv({
            label,
            unitTypeField,
            formatter,
            propertyAggregate = "sum",
            unitTypeFieldWeights,
            showTradeOutPercent = false
    }:
    {
        label: string,
        unitTypeField: UnitTypeFieldKey,
        formatter: Intl.NumberFormat,
        propertyAggregate?: "sum" | "wavg" | "ratio",
        unitTypeFieldWeights?: UnitTypeFieldKey,
        showTradeOutPercent?: boolean
    }
    ): string[][] {
        const revenueData = props.unitTypeData;
        if (!props.property || !revenueData) {
            return [];
        }

        let propertyNumbers: number[] = [];
        if (propertyAggregate == "sum") {
            propertyNumbers = buildPropertyNumbersSum(
                    props.property.reforecastStartMonthIndex,
                    revenueData[unitTypeField],
                    selectedUnitType);
        }
        else if (propertyAggregate == "wavg" && unitTypeFieldWeights) {
            propertyNumbers = buildPropertyNumbersWeightedAverage(
                    props.property.reforecastStartMonthIndex,
                    revenueData[unitTypeField],
                    revenueData[unitTypeFieldWeights],
                    selectedUnitType);
        }
        else if (propertyAggregate == "ratio" && unitTypeFieldWeights) {
            const numeratorPropertyNumbers = buildPropertyNumbersSum(
                    props.property.reforecastStartMonthIndex,
                    revenueData[unitTypeField],
                    selectedUnitType);
            const denominatorPropertyNumbers = buildPropertyNumbersSum(
                    props.property.reforecastStartMonthIndex,
                    revenueData[unitTypeFieldWeights],
                    selectedUnitType);
            propertyNumbers = buildRatio(numeratorPropertyNumbers, denominatorPropertyNumbers);
        }
        else {
            return [];
        }

        const reforecastStartMonthIndex = props.property.reforecastStartMonthIndex;
        const inPlaceRentByUnitType = revenueData.unitTypeInPlaceRent.toIdMap("unitTypeId");

        const propertyRows: string[][] = [[label, ...buildCellsCsv(reforecastStartMonthIndex, propertyNumbers, formatter)]];
        if (showTradeOutPercent) {
            propertyRows.push([
                `${label} %`,
                ...buildTradeOutPercentCellsCsv(reforecastStartMonthIndex, buildPropertyTradeOutPercent(revenueData, propertyNumbers), formatterPercentNoGrouping)
            ]);
        }

        const unitTypeRows = revenueData[unitTypeField]
            .filter(utData => selectedUnitType == "all" || utData.unitTypeId == selectedUnitType)
            .sortBy("unitTypeName").flatMap(utData => {
                const unitTypeNumbers = buildNumbers(reforecastStartMonthIndex, utData);
                const rows: string[][] = [[` ${utData.unitTypeName}`, ...buildCellsCsv(reforecastStartMonthIndex, unitTypeNumbers, formatter)]];
                const unitTypeInPlaceRent = inPlaceRentByUnitType[utData.unitTypeId];
                if (showTradeOutPercent && unitTypeInPlaceRent) {
                    rows.push([
                        ` ${utData.unitTypeName} %`,
                        ...buildTradeOutPercentCellsCsv(
                            reforecastStartMonthIndex,
                            buildTradeOutPercent(unitTypeNumbers, buildNumbers(reforecastStartMonthIndex, unitTypeInPlaceRent)),
                            formatterPercentNoGrouping)
                    ]);
                }
                return rows;
            });

        return [...propertyRows, ...unitTypeRows];
    }

    function SubHead(args: {title: string}): ReactElement {
        if (!props.property) {
            return <></>;
        }
        return (
            <Row style={{backgroundColor: "var(--grey-200)"}}>
                <Cell
                    // colSpan={24 - props.property.reforecastStartMonthIndex + 2}
                    style={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "var(--grey-200)",
                    }}> {args.title} </Cell>
                <Cell
                    colSpan={23 - props.property.reforecastStartMonthIndex + 2}
                    style={{
                        backgroundColor: "var(--grey-200)",
                    }}></Cell>
            </Row>
        );
    }

    function exportToCsv(): void {
        if (!props.property || !props.unitTypeData) {
            return;
        }

        function toString(arr: string[]): string {
            return arr.join(",");
        }

        // The number of hacks is a travesty at this point, but I don't want to rewrite the whole thing
        // now. If any more changes are required to this export, a full rewrite is REQUIRED.
        // DO NOT APPROVE ANY MORE HACKS.
        function keepOnlyBudgetCells(cells: string[], forceBudgetCells = false): string[] {
            if((forceBudgetCells || props.hideReforecast) && cells.length > 12) {
                const budget = cells.slice(-12);
                budget.unshift(cells[0] as string);
                return budget;
            } else if(!props.hideReforecast && cells.length > 24 && props.property.isAcquisitionProperty) {
                const budget = cells.slice(-24, -12);
                budget.unshift(cells[0] as string);
                return budget;
            }
            return cells;
        }

        const csvRows = [
            keepOnlyBudgetCells(buildHeaderCellsCsv(props.property.reforecastStartMonthIndex, propertyDisplayBudgetYear(props.property), getMonths(props.property)), props.property.isAcquisitionProperty).join(",")
        ];

        csvRows.push("METRICS");
        csvRows.push(...tableBlockRowExpandableCsv({label: "Unit Count", unitTypeField: "unitTypeUnitCountMonthly", formatter: formatterIntNoGrouping}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(...tableBlockRowExpandableCsv({label: "Expiration Count", unitTypeField: "unitTypeExpirationCount", formatter: formatterIntNoGrouping}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(...tableBlockRowRatioCsv({label: "Occupied %", unitTypeNumeratorField: "unitTypeOccupiedCount", unitTypeDenominatorField: "unitTypeUnitCountMonthly", formatter: formatterPercentNoGrouping}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(...tableBlockRowExpandableCsv({label: "Occupied Count", unitTypeField: "unitTypeOccupiedCount", formatter: formatterIntNoGrouping}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(...tableBlockRowRatioCsv({label: "Renewal %", unitTypeNumeratorField: "unitTypeRenewalCount", unitTypeDenominatorField: "unitTypeExpirationCount", formatter: formatterPercentNoGrouping}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(...tableBlockRowExpandableCsv({label: "Renewal Count", unitTypeField: "unitTypeRenewalCount", formatter: formatterIntNoGrouping}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(...tableBlockRowExpandableCsv({label: "Move In Count", unitTypeField: "unitTypeMoveInCount", formatter: formatterDecimalNoGrouping}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(keepOnlyBudgetCells(["Total Move In Count", ...buildCellsCsv(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.totalMoveInCount), formatterDecimalNoGrouping)]).join(","));
        csvRows.push(...tableBlockRowRatioCsv({label: "Move Out %", unitTypeNumeratorField: "unitTypeMoveOutCount", unitTypeDenominatorField: "unitTypeExpirationCount", formatter: formatterPercentNoGrouping}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(...tableBlockRowExpandableCsv({label: "Move Out Count", unitTypeField: "unitTypeMoveOutCount", formatter: formatterDecimalNoGrouping}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(keepOnlyBudgetCells(["Early Term Count", ...buildCellsCsv(props.property.reforecastStartMonthIndex, buildNumbers( props.property.reforecastStartMonthIndex, props.unitTypeData.earlyTerminationCount), formatterIntNoGrouping)]).join(","));
        csvRows.push(keepOnlyBudgetCells(["MTM Move Out Count",...buildCellsCsv(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.m2mMoveOutCount),formatterIntNoGrouping)]).join(","));
        csvRows.push(keepOnlyBudgetCells(["Total Move Out Count", ...buildCellsCsv(props.property.reforecastStartMonthIndex, buildNumbers( props.property.reforecastStartMonthIndex, props.unitTypeData.totalMoveOutCount), formatterDecimalNoGrouping)]).join(","));
        csvRows.push("AMOUNTS ($)");
        csvRows.push(...tableBlockRowExpandableCsv({label: "Avg Market Rent", unitTypeField: "unitTypeAvgMarketRent", formatter: formatterIntNoGrouping, propertyAggregate: "wavg", unitTypeFieldWeights: "unitTypeUnitCountMonthly"}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(...tableBlockRowExpandableCsv({label: "In Place Rent", unitTypeField: "unitTypeInPlaceRent", formatter: formatterIntNoGrouping, propertyAggregate: "wavg", unitTypeFieldWeights: "unitTypeUnitCountMonthly"}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(...tableBlockRowExpandableCsv({label: "New Lease Tradeout", unitTypeField: "unitTypeNewLeaseTradeOut", formatter: formatterIntNoGrouping, propertyAggregate: "wavg", unitTypeFieldWeights: "unitTypeMoveInCount", showTradeOutPercent: true}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push(...tableBlockRowExpandableCsv({label: "Renewal Tradeout", unitTypeField: "unitTypeRenewalTradeOut", formatter: formatterIntNoGrouping, propertyAggregate: "wavg", unitTypeFieldWeights: "unitTypeRenewalCount", showTradeOutPercent: true}).map(v => toString(keepOnlyBudgetCells(v))));
        csvRows.push("FINANCIALS ($)");
        csvRows.push(keepOnlyBudgetCells(["Market Rent",...buildCellsCsv(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.rent),formatterIntNoGrouping)]).join(","));
        if(props.property.originalRevenueModelSubsidyEnabled) {
            csvRows.push(keepOnlyBudgetCells(["Rent Subsidy",...buildCellsCsv(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.rentSubsidy),formatterIntNoGrouping)]).join(","));
        }
        csvRows.push(keepOnlyBudgetCells(["Loss To Lease",...buildCellsCsv(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.lossToLease),formatterIntNoGrouping)]).join(","));
        csvRows.push(keepOnlyBudgetCells(["Gross Potential Rent",...buildCellsCsv(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.grossPotentialRent),formatterIntNoGrouping)]).join(","));
        csvRows.push(keepOnlyBudgetCells(["GPR Change",...buildCellsCsv(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.momLossToLeaseChange),formatterIntNoGrouping)]).join(","));

        const saveData = new Blob([csvRows.join("\n")], {type: "text/csv;charset=utf-8"});

        const budgetYear = props.property.isAcquisitionProperty ? props.property.acquisitionBudgetYear : props.property.budgetYear;
        FileSaver.saveAs(saveData, `${budgetYear} - ${props.property.name}_${formatExportTimestampSlug(new Date())}.csv`);
    }

    return (
        !(props.unitTypeData && props.property && props.unitTypes) ?
        <></>
        :   <div className={cn(tableCSS.wrapper, tableCSS.revTable)}>
                <div className={tableCSS.topHeader}>
                    {props.showLabel && <h4 className={css.label}>{props.property.name} | Revenue Summary</h4>}
                    <div className={tableCSS.headerActions}>
                        <div className={css.unitTypeSelector}>
                            <Dropdown
                                selectedItem={selectedUnitType}
                                onSelect={(v) => setSelectedUnitType(v)}
                            >
                                <Field>
                                    <Select>
                                        {props.unitTypes.find(ut => ut.unitTypeId == selectedUnitType)?.unitTypeName ?? "N/A"}
                                    </Select>
                                </Field>
                                <Menu>
                                    {props.unitTypes.map(({ unitTypeId, unitTypeName }) => (
                                        <Item key={unitTypeId} value={unitTypeId}>
                                            {unitTypeName}
                                        </Item>
                                    ))}
                                </Menu>
                            </Dropdown>
                        </div>
                        <Button onClick={() => exportToCsv()} style={{marginLeft: "1rem"}}>Export</Button>
                    </div>
                </div>
                <div className={tableCSS.tableWrapper}>
                    <Table className={tableCSS.table}>
                        <Head isSticky style={{zIndex: 3}}>
                            <HeaderRow className={tableCSS.headerRow}>
                                {buildHeaderCells(
                                        props.property.reforecastStartMonthIndex,
                                        propertyDisplayBudgetYear(props.property),
                                        getMonths(props.property),
                                        hideReforecast,
                                        props.property.isAcquisitionProperty,
                                )}
                            </HeaderRow>
                        </Head>
                        <Body>
                            <SubHead title="Metrics" />
                            <TableBlockRowExpandable
                                key={"Unit Count"}
                                label={"Unit Count"}
                                unitTypeField={"unitTypeUnitCountMonthly"}
                                formatter={formatterInt}
                                hideReforecast={hideReforecast}
                            />
                            <TableBlockRowExpandable
                                key={"Expiration Count"}
                                label={"Expiration Count"}
                                unitTypeField={"unitTypeExpirationCount"}
                                formatter={formatterInt}
                                hideReforecast={hideReforecast}
                            />
                            <TableBlockRowRatio
                                key="Occupied %"
                                label="Occupied %"
                                unitTypeNumeratorField="unitTypeOccupiedCount"
                                unitTypeDenominatorField="unitTypeUnitCountMonthly"
                                formatter={formatterPercent}
                                hideReforecast={hideReforecast}
                            />
                            <TableBlockRowExpandable
                                key={"Occupied Count"}
                                label={"Occupied Count"}
                                unitTypeField={"unitTypeOccupiedCount"}
                                formatter={formatterInt}
                                hideReforecast={hideReforecast}
                            />
                            <TableBlockRowRatio
                                key="Renewal %"
                                label="Renewal %"
                                unitTypeNumeratorField="unitTypeRenewalCount"
                                unitTypeDenominatorField="unitTypeExpirationCount"
                                formatter={formatterPercent}
                                hideReforecast={hideReforecast}
                            />
                            <TableBlockRowExpandable
                                key={"Renewal Count"}
                                label={"Renewal Count"}
                                unitTypeField={"unitTypeRenewalCount"}
                                formatter={formatterInt}
                                hideReforecast={hideReforecast}
                            />
                            <TableBlockRowExpandable
                                key={"Move In Count"}
                                label={"Move In Count"}
                                unitTypeField={"unitTypeMoveInCount"}
                                formatter={formatterInt}
                                hideReforecast={hideReforecast}
                            />
                            <Row className={selectedUnitType !== "all" ? css.increasedRowOpacity : undefined}>
                                <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>Total Move In Count</Cell>
                                {buildCells(props.property.reforecastStartMonthIndex,
                                            buildNumbers(
                                                    props.property.reforecastStartMonthIndex,
                                                    props.unitTypeData.totalMoveInCount),
                                            formatterInt,
                                            hideReforecast,
                                            props.property.isAcquisitionProperty,
                                            )
                                }
                            </Row>
                            <TableBlockRowRatio
                                key="Move Out %"
                                label="Move Out %"
                                unitTypeNumeratorField="unitTypeMoveOutCount"
                                unitTypeDenominatorField="unitTypeExpirationCount"
                                formatter={formatterPercent}
                                hideReforecast={hideReforecast}
                            />
                            <TableBlockRowExpandable
                                key={"Move Out Count"}
                                label={"Move Out Count"}
                                unitTypeField={"unitTypeMoveOutCount"}
                                formatter={formatterInt}
                                hideReforecast={hideReforecast}
                            />
                            <Row className={selectedUnitType !== "all" ? css.increasedRowOpacity : undefined}>
                                <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>Early Term Count</Cell>
                                {buildCells(props.property.reforecastStartMonthIndex,
                                            buildNumbers(
                                                    props.property.reforecastStartMonthIndex,
                                                    props.unitTypeData.earlyTerminationCount),
                                            formatterInt,
                                            hideReforecast,
                                            props.property.isAcquisitionProperty,
                                            )
                                }
                            </Row>
                            <Row className={selectedUnitType !== "all" ? css.increasedRowOpacity : undefined}>
                                <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>MTM Move Out Count</Cell>
                                {buildCells(props.property.reforecastStartMonthIndex,
                                            buildNumbers(
                                                    props.property.reforecastStartMonthIndex,
                                                    props.unitTypeData.m2mMoveOutCount),
                                            formatterInt,
                                            hideReforecast,
                                            props.property.isAcquisitionProperty,
                                            )
                                }
                            </Row>
                            <Row className={selectedUnitType !== "all" ? css.increasedRowOpacity : undefined}>
                                <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>Total Move Out Count</Cell>
                                {buildCells(props.property.reforecastStartMonthIndex,
                                            buildNumbers(
                                                    props.property.reforecastStartMonthIndex,
                                                    props.unitTypeData.totalMoveOutCount),
                                            formatterInt,
                                            hideReforecast,
                                            props.property.isAcquisitionProperty,
                                            )
                                }
                            </Row>

                            <SubHead title="AMOUNTS"/>

                            <TableBlockRowExpandable
                                key={"Avg Market Rent"}
                                label={"Avg Market Rent"}
                                unitTypeField={"unitTypeAvgMarketRent"}
                                formatter={formatterDollarUSNoDecimal}
                                propertyAggregate="wavg"
                                unitTypeFieldWeights={"unitTypeUnitCountMonthly"}
                                hideReforecast={hideReforecast}
                            />
                            <TableBlockRowExpandable
                                key={"In Place Rent"}
                                label={"In Place Rent"}
                                unitTypeField={"unitTypeInPlaceRent"}
                                formatter={formatterDollarUSNoDecimal}
                                propertyAggregate="wavg"
                                unitTypeFieldWeights={"unitTypeUnitCountMonthly"}
                                hideReforecast={hideReforecast}
                            />
                            <TableBlockRowExpandable
                                key={"New Lease Tradeout"}
                                label={"New Lease Tradeout"}
                                unitTypeField={"unitTypeNewLeaseTradeOut"}
                                formatter={formatterDollarUSNoDecimal}
                                propertyAggregate="wavg"
                                unitTypeFieldWeights={"unitTypeMoveInCount"}
                                hideReforecast={hideReforecast}
                                showTradeOutPercent
                            />
                            <TableBlockRowExpandable
                                key={"Renewal Tradeout"}
                                label={"Renewal Tradeout"}
                                unitTypeField={"unitTypeRenewalTradeOut"}
                                formatter={formatterDollarUSNoDecimal}
                                propertyAggregate="wavg"
                                unitTypeFieldWeights={"unitTypeRenewalCount"}
                                hideReforecast={hideReforecast}
                                showTradeOutPercent
                            />

                            <SubHead title="Financials"/>

                            <Row>
                                <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>Market Rent</Cell>
                                {buildCells(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.rent), formatterDollarUSNoDecimal, hideReforecast, props.property.isAcquisitionProperty)}
                            </Row>
                            {props.property.originalRevenueModelSubsidyEnabled && <Row>
                                <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>Rent Subsidy</Cell>
                                {buildCells(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.rentSubsidy), formatterDollarUSNoDecimal, hideReforecast, props.property.isAcquisitionProperty)}
                            </Row>}
                            <Row>
                                <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>Loss To Lease</Cell>
                                {buildCells(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.lossToLease), formatterDollarUSNoDecimal, hideReforecast, props.property.isAcquisitionProperty)}
                            </Row>
                            <Row>
                                <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>Gross Potential Rent</Cell>
                                {buildCells(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.grossPotentialRent), formatterDollarUSNoDecimal, hideReforecast, props.property.isAcquisitionProperty)}
                            </Row>
                            <Row style={{fontStyle: "italic"}}>
                                <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>GPR Change</Cell>
                                {buildCells(props.property.reforecastStartMonthIndex, buildNumbers(props.property.reforecastStartMonthIndex, props.unitTypeData.momLossToLeaseChange), formatterDollarUSNoDecimal, hideReforecast, props.property.isAcquisitionProperty)}
                            </Row>
                        </Body>
                    </Table>
                </div>
            </div>
    );
}